import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudOff, Grid3x3, RefreshCw } from 'lucide-react';
import { cn } from '@clarix/ui';
import { fetchAppStatus } from '@clarix/api-client';
import { env } from '@/config/env';

interface AppUnavailableProps {
  /** Display name, e.g. "Projects". */
  appName: string;
  /** Registry slug used for the status probe, e.g. "projects". */
  appSlug: string;
  /** Remount the app subtree — called on manual retry and on auto-recovery. */
  onRetry: () => void;
}

/** How often to re-probe this app's own status while the panel is up. */
const RECOVERY_POLL_MS = 5_000;

/**
 * ONE app's backend is down — everything else still works.
 *
 * Deliberately NOT the full-screen <MaintenancePage>. That page is `fixed
 * inset-0` and covers the TopBar, rail and switcher, which is right for a
 * platform outage and wrong here: if projects-api dies, the user must still be
 * able to switch to Payroll. So this renders INSIDE the content area, leaving
 * the shell chrome live, and it names the app so the failure reads as scoped
 * rather than "Clarix is down".
 *
 * It polls `GET /system/status/<slug>` — this app's own status, never the
 * platform one — and remounts the app the moment its service answers again.
 */
export function AppUnavailable({ appName, appSlug, onRetry }: AppUnavailableProps) {
  const [isChecking, setIsChecking] = useState(false);
  // onRetry identity changes on each parent render; a ref keeps the poll effect
  // from tearing down and restarting its interval every time. Assigned in an
  // effect, not during render (same pattern as WorkspaceUnavailable).
  const retryRef = useRef(onRetry);
  useEffect(() => {
    retryRef.current = onRetry;
  });

  /** Probe once. Returns true (and remounts the app) if it has recovered. */
  const check = useCallback(async (): Promise<boolean> => {
    try {
      const status = await fetchAppStatus(env.VITE_API_BASE_URL, appSlug);
      if (status.available) {
        retryRef.current();
        return true;
      }
    } catch {
      // Still unreachable — expected during the outage window.
    }
    return false;
  }, [appSlug]);

  const handleRetry = async () => {
    setIsChecking(true);
    const recovered = await check();
    // Recovered → the parent remounts us away, so don't touch state.
    if (!recovered) setIsChecking(false);
  };

  useEffect(() => {
    const id = window.setInterval(() => void check(), RECOVERY_POLL_MS);
    return () => window.clearInterval(id);
  }, [check]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 ring-1 ring-amber-100">
          <CloudOff className="h-7 w-7 text-amber-500" strokeWidth={1.5} />
        </div>

        <h1 className="mb-3 text-4xl font-semibold tracking-tight text-gray-900">
          {appName} is temporarily unavailable
        </h1>

        <p className="mb-2 text-xl leading-relaxed text-gray-500">
          We can&apos;t reach the {appName} service right now. This affects {appName} only —
          your other apps are still working.
        </p>

        <p className="mb-8 text-lg text-gray-400">
          Checking again automatically&hellip;
        </p>

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleRetry}
            disabled={isChecking}
            className={cn(
              'inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-xl font-medium text-white shadow-md shadow-indigo-200 transition-all',
              'hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-200 hover:-translate-y-0.5',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-md',
            )}
          >
            <RefreshCw className={cn('h-4 w-4', isChecking && 'animate-spin')} strokeWidth={1.5} />
            {isChecking ? 'Checking...' : 'Try again'}
          </button>

          {/* The whole point of a scoped outage: an easy way to somewhere that works. */}
          <Link
            to="/"
            className={cn(
              'inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-xl font-medium text-gray-700 transition-all',
              'hover:border-gray-300 hover:bg-gray-50',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
            )}
          >
            <Grid3x3 className="h-4 w-4" strokeWidth={1.5} />
            Other apps
          </Link>
        </div>
      </div>
    </div>
  );
}
